import { ChangeEvent, FC, useState } from "react";
import { useDispatch } from "react-redux";

import { selectFormConfirmationSettings } from "./../../../store/settings/selectors";
import { changeConfirmationSettings } from "./../../../store/settings/actionCreators";
import { IEmailEditorData } from "../../../store/settings/types";
import useShallowSelector from "./../../../hooks/useShalowSelector";
import Switch from "./../../../libs/Switch";
import Radio from "./../../../libs/Radio";
import ConfirmationEditor from "./../Notification/RespondentNotification/NotificationTextEditor";

import styles from "./styles.module.scss";

const Confirmation: FC = () => {
    const dispatch = useDispatch();
    const confirmationSettings = useShallowSelector(selectFormConfirmationSettings);

    const [, setAddMoreAnswer] = useState<boolean>(confirmationSettings.afterSubmit.addMoreAnswer.isActive);
    const [, setDownloadPDF] = useState<boolean>(confirmationSettings.afterSubmit.downloadPDF.isActive);
    const [, setEditAnswer] = useState<boolean>(confirmationSettings.afterSubmit.editAnswer.isActive);

    const [enterURL, setEnterURL] = useState<string>(confirmationSettings.afterSubmit.enterURL);
    const [message, setMessage] = useState<IEmailEditorData>({
        html: confirmationSettings.afterSubmit.message,
        json: confirmationSettings.afterSubmit.json,
    });

    const [addMoreAnswerLabel, setAddMoreAnswerLabel] = useState<string>(
        confirmationSettings.afterSubmit.addMoreAnswer.buttonLabel,
    );
    const [downloadPDFLabel, setDownloadPDFLabel] = useState<string>(
        confirmationSettings.afterSubmit.downloadPDF.buttonLabel,
    );
    const [editAnswerLabel, setEditAnswerLabel] = useState<string>(
        confirmationSettings.afterSubmit.editAnswer.buttonLabel,
    );

    const getEnterURL = (e: ChangeEvent<HTMLInputElement>) => setEnterURL(e.target.value);
    const getAddMoreAnswerLabel = (e: ChangeEvent<HTMLInputElement>) => setAddMoreAnswerLabel(e.target.value);
    const getDownloadPDFLabel = (e: ChangeEvent<HTMLInputElement>) => setDownloadPDFLabel(e.target.value);
    const getEditAnswerLabel = (e: ChangeEvent<HTMLInputElement>) => setEditAnswerLabel(e.target.value);

    const handleStateUpdate = (element: any) => {
        dispatch(
            changeConfirmationSettings({
                prevState: confirmationSettings,
                currentState: {
                    afterSubmit: {
                        ...confirmationSettings.afterSubmit,
                        ...element,
                    },
                },
            }),
        );
    };

    return (
        <>
            <div className={styles.section}>
                <div className={styles.subBox}>
                    <p className={styles.subTitle}> After Submission </p>

                    <Radio
                        checked={confirmationSettings.afterSubmit.isActive}
                        onChange={() =>
                            handleStateUpdate({
                                ...confirmationSettings.afterSubmit,
                                isActive: true,
                            })
                        }
                        label="Show a Thank You Page"
                        labelClassName={styles.label}
                    />

                    <Radio
                        checked={!confirmationSettings.afterSubmit.isActive}
                        onChange={() =>
                            handleStateUpdate({
                                ...confirmationSettings.afterSubmit,
                                isActive: false,
                            })
                        }
                        label="Redirect To URL"
                        labelClassName={styles.label}
                    />
                </div>

                {!confirmationSettings.afterSubmit.isActive && (
                    <>
                        <div className={styles.subBox}>
                            <p className={styles.subTitle}> Enter URL </p>

                            <input
                                type="text"
                                value={enterURL}
                                onChange={e => getEnterURL(e)}
                                className={styles.customInput}
                                onBlur={() =>
                                    handleStateUpdate({
                                        ...confirmationSettings.afterSubmit,
                                        enterURL: enterURL,
                                    })
                                }
                            />
                        </div>

                        <div className={styles.subBox}>
                            <p className={styles.subTitle}> Open URL in </p>

                            <Radio
                                checked={confirmationSettings.afterSubmit.openURL}
                                onChange={() =>
                                    handleStateUpdate({
                                        ...confirmationSettings.afterSubmit,
                                        openURL: true,
                                    })
                                }
                                label="New Tab"
                                labelClassName={styles.label}
                            />
                            <Radio
                                checked={!confirmationSettings.afterSubmit.openURL}
                                onChange={() =>
                                    handleStateUpdate({
                                        ...confirmationSettings.afterSubmit,
                                        openURL: false,
                                    })
                                }
                                label="Same Tab"
                                labelClassName={styles.label}
                            />
                        </div>
                    </>
                )}

                {!(!confirmationSettings.afterSubmit.isActive && !confirmationSettings.afterSubmit.openURL) && (
                    <div className={styles.subBox}>
                        <p className={styles.subTitle}>Message</p>

                        <ConfirmationEditor
                            invoiceInitial={message}
                            addMoreAnswer={confirmationSettings.afterSubmit.addMoreAnswer}
                            downloadPDF={confirmationSettings.afterSubmit.downloadPDF}
                            editAnswer={confirmationSettings.afterSubmit.editAnswer}
                            handleChange={e => {
                                setMessage(e);
                                handleStateUpdate({
                                    ...confirmationSettings.afterSubmit,
                                    message: e,
                                });
                            }}
                        />
                    </div>
                )}
            </div>

            {!(!confirmationSettings.afterSubmit.isActive && !confirmationSettings.afterSubmit.openURL) && (
                <>
                    <div className={styles.line} />

                    <div className={styles.section}>
                        <label className={styles.switchContainer}>
                            <Switch
                                checked={confirmationSettings.afterSubmit.addMoreAnswer.isActive}
                                onChange={() => {
                                    setAddMoreAnswer(state => !state);

                                    handleStateUpdate({
                                        addMoreAnswer: {
                                            ...confirmationSettings.afterSubmit.addMoreAnswer,
                                            isActive: !confirmationSettings.afterSubmit.addMoreAnswer.isActive,
                                        },
                                    });
                                }}
                            />

                            <p className={styles.subTitle}>Fill Again</p>
                        </label>

                        {confirmationSettings.afterSubmit.addMoreAnswer.isActive && (
                            <div className={styles.subBox}>
                                <p className={styles.subTitle}>Button Label</p>

                                <input
                                    type="text"
                                    value={addMoreAnswerLabel}
                                    onChange={e => getAddMoreAnswerLabel(e)}
                                    className={styles.customInput}
                                    onBlur={() =>
                                        handleStateUpdate({
                                            ...confirmationSettings.afterSubmit,
                                            addMoreAnswer: {
                                                ...confirmationSettings.afterSubmit.addMoreAnswer,
                                                buttonLabel: addMoreAnswerLabel,
                                            },
                                        })
                                    }
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles.line} />

                    <div className={styles.section}>
                        <label className={styles.switchContainer}>
                            <Switch
                                checked={confirmationSettings.afterSubmit.downloadPDF.isActive}
                                onChange={() => {
                                    setDownloadPDF(state => !state);
                                    handleStateUpdate({
                                        downloadPDF: {
                                            ...confirmationSettings.afterSubmit.downloadPDF,
                                            isActive: !confirmationSettings.afterSubmit.downloadPDF.isActive,
                                        },
                                    });
                                }}
                            />

                            <p className={styles.subTitle}>Download PDF</p>
                        </label>

                        {confirmationSettings.afterSubmit.downloadPDF.isActive && (
                            <div className={styles.subBox}>
                                <p className={styles.subTitle}>Button Label</p>

                                <input
                                    type="text"
                                    value={downloadPDFLabel}
                                    onChange={e => getDownloadPDFLabel(e)}
                                    className={styles.customInput}
                                    onBlur={() =>
                                        handleStateUpdate({
                                            ...confirmationSettings.afterSubmit,
                                            downloadPDF: {
                                                ...confirmationSettings.afterSubmit.downloadPDF,
                                                buttonLabel: downloadPDFLabel,
                                            },
                                        })
                                    }
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles.line} />

                    <div className={styles.section}>
                        <label className={styles.switchContainer}>
                            <Switch
                                checked={confirmationSettings.afterSubmit.editAnswer.isActive}
                                onChange={() => {
                                    setEditAnswer(state => !state);
                                    handleStateUpdate({
                                        editAnswer: {
                                            ...confirmationSettings.afterSubmit.editAnswer,
                                            isActive: !confirmationSettings.afterSubmit.editAnswer.isActive,
                                        },
                                    });
                                }}
                            />

                            <p className={styles.subTitle}>Edit Answer</p>
                        </label>

                        {confirmationSettings.afterSubmit.editAnswer.isActive && (
                            <div className={styles.subBox}>
                                <p className={styles.subTitle}>Button Label</p>

                                <input
                                    type="text"
                                    value={editAnswerLabel}
                                    onChange={e => getEditAnswerLabel(e)}
                                    className={styles.customInput}
                                    onBlur={() =>
                                        handleStateUpdate({
                                            ...confirmationSettings.afterSubmit,
                                            editAnswer: {
                                                ...confirmationSettings.afterSubmit.editAnswer,
                                                buttonLabel: editAnswerLabel,
                                            },
                                        })
                                    }
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default Confirmation;
